/* ============================================================
   Authenticated API client. Every request attaches a fresh Cognito ID token
   (auto-refreshed by AuthContext.getIdToken) as `Authorization: Bearer <jwt>` —
   which the API Gateway Cognito authorizer validates before the request ever
   reaches the Lambda. Paths are relative to `baseUrl` and appended verbatim,
   e.g. api.get('/v1/sync?today=2026-08-15'). `baseUrl` is a bare origin when
   infra sets a custom API domain, and carries the stage segment when it falls
   back to the execute-api host — either way the path below is what the Lambda
   sees.

   A plain function, not a hook (that's api/useApi.ts), so the outbox, the
   sign-out push detach and the tests can hold one without a React tree.
   ============================================================ */
import type { UpdateSignalAdapter } from '../adapters';

/** Thrown on any non-2xx response. `body` is the parsed JSON error, if any. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type TokenGetter = () => Promise<string | null>;

export interface ApiClient {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown): Promise<T>;
  /** DELETE. Some endpoints (e.g. /v1/push-tokens) take a JSON body identifying
      what to remove — pass it as the optional second argument. */
  del(path: string, body?: unknown): Promise<void>;
}

export interface ApiClientOptions {
  baseUrl: string;
  getIdToken: TokenGetter;
  /** Who is calling (`X-App-Platform` / `X-App-Build`), so the server can
     refuse a build below its floor with a 426 (backend/src/client-version.ts).
     The native build number, not the JS version: a build too old to fix is
     exactly the case an OTA update can't reach. Omitted where there's no
     native build (web), which the server allows. */
  headers?: Record<string, string>;
  /** Where the server's verdict on this build is reported. Optional: a
     platform with no store listing has nothing to show for it. */
  updates?: UpdateSignalAdapter;
}

function errorMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: unknown }).error;
    if (typeof e === 'string') return e;
  }
  return `Request failed (${status})`;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const { baseUrl, getIdToken, headers: clientHeaders = {}, updates } = opts;

  async function request(method: string, path: string, body?: unknown): Promise<Response> {
    const token = await getIdToken();
    if (!token) throw new ApiError(401, 'Not authenticated', null);

    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...clientHeaders,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    // Below the server's RECOMMENDED build: a soft "new version" nudge rides on
    // any response, success or error (UpdateGate shows it, at most daily).
    if (updates && res.headers.get('X-App-Update') === 'recommended') {
      updates.onUpdateRecommended(
        res.headers.get('X-App-Recommended-Build'),
        res.headers.get('X-App-Store-Url'),
      );
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let parsed: unknown = text;
      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch {
          // non-JSON body — keep the raw text
        }
      }
      // 426 = this build is below the server's floor; UpdateGate takes over the
      // screen. Still thrown, so callers (and the outbox) see a failure.
      if (res.status === 426) updates?.onUpgradeRequired(parsed);
      throw new ApiError(res.status, errorMessage(res.status, parsed), parsed);
    }
    return res;
  }

  async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await request(method, path, body);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    get<T = unknown>(path: string) {
      return json<T>('GET', path);
    },
    post<T = unknown>(path: string, body?: unknown) {
      return json<T>('POST', path, body);
    },
    put<T = unknown>(path: string, body?: unknown) {
      return json<T>('PUT', path, body);
    },
    patch<T = unknown>(path: string, body?: unknown) {
      return json<T>('PATCH', path, body);
    },
    async del(path: string, body?: unknown) {
      await request('DELETE', path, body);
    },
  };
}
